import {
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import axios from "axios";
import * as React from "react";

import { LoadingWrapper } from "../../../components/LoadingWrapper";
import { Config } from "../../../config/config";
import { Helpers } from "../../../utils/helpers";
import { PopupMessage } from "../../../utils/message";

interface AllianceWarSeason {
    id: string;
    name: string;
    status: string;
    configId: number;
    rewardConfigId: number;
    startDate: string;
    endDate: string;
}

interface AllianceWarPreparationItem {
    id: string;
    name: string;
    country: string | null;
    description: string | null;
    tag: string | null;
    membersCount: number;
    usersEnlist: number;
    layoutDone: boolean;
    participantsDone: boolean;
    isReady: boolean;
    leaderUserId: string | null;
    leaderWebUserId: string | null;
    leaderName: string | null;
    sessionId: string;
    squadId: string;
    preparationId: string | null;
    status: string | null;
    volunteerCount: number;
}

interface PreparationsResponse {
    success: boolean;
    seasonId: string;
    season?: AllianceWarSeason;
    data: {
        items: AllianceWarPreparationItem[];
        total: number;
        page: number;
        pageSize: number;
    };
    message?: string;
}

const initialFilters = {
    squadId: "",
    preparationId: "",
};

const boolChip = (value: boolean) => (
    <Chip
        size="small"
        label={value ? "TRUE" : "FALSE"}
        style={{
            backgroundColor: value ? "#2e7d32" : "#757575",
            color: "#fff",
        }}
    />
);

export const AllianceWarPreparationsDetail = (props: any) => {
    const seasonId = props.match?.params?.seasonId;
    const [season, setSeason] = React.useState<AllianceWarSeason | null>(null);
    const [items, setItems] = React.useState<AllianceWarPreparationItem[]>([]);
    const [filters, setFilters] = React.useState(initialFilters);
    const [appliedFilters, setAppliedFilters] = React.useState(initialFilters);
    const [page, setPage] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(10);
    const [total, setTotal] = React.useState(0);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const [applyingId, setApplyingId] = React.useState<string | null>(null);
    const [applyingAll, setApplyingAll] = React.useState(false);
    const [refreshingTroops, setRefreshingTroops] = React.useState(false);
    const [confirmUseDefaultAllOpen, setConfirmUseDefaultAllOpen] = React.useState(false);
    const [confirmRefreshTroopsOpen, setConfirmRefreshTroopsOpen] = React.useState(false);

    React.useEffect(() => {
        document.title = Helpers.documentTitle("Alliance Wars / Preparations");
        if (seasonId) {
            loadPreparations(1, pageSize, initialFilters);
        }
    }, [seasonId]);

    const loadPreparations = (
        nextPage: number,
        nextPageSize = pageSize,
        nextFilters = appliedFilters,
    ) => {
        if (!seasonId) {
            return;
        }

        setLoading(true);
        setError(null);

        axios.get<PreparationsResponse>(
            `${Config.getInstance.apiUrl}/api/alliance_wars/seasons/${seasonId}/preparations`,
            {
                headers: {
                    Authorization: Helpers.getSessionId(),
                },
                params: {
                    page: nextPage,
                    pageSize: nextPageSize,
                    squadId: nextFilters.squadId || undefined,
                    preparationId: nextFilters.preparationId || undefined,
                },
            },
        )
            .then((response) => {
                if (!response.data?.success) {
                    const message = response.data?.message || "Failed to fetch preparations";
                    setError(message);
                    PopupMessage.error(message);
                    setItems([]);
                    setTotal(0);
                    return;
                }

                setSeason(response.data.season || null);
                setItems(response.data.data?.items || []);
                setTotal(response.data.data?.total || 0);
                setPage((response.data.data?.page || nextPage) - 1);
                setPageSize(response.data.data?.pageSize || nextPageSize);
                setAppliedFilters(nextFilters);
            })
            .catch((err) => {
                const message = err?.response?.data?.message || err?.message || "Failed to fetch preparations";
                setError(message);
                PopupMessage.error(message);
                setItems([]);
                setTotal(0);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleFilterChange = (key: keyof typeof initialFilters) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setFilters({
            ...filters,
            [key]: event.target.value,
        });
    };

    const handleSearch = () => {
        loadPreparations(1, pageSize, filters);
    };

    const handleClear = () => {
        setFilters(initialFilters);
        loadPreparations(1, pageSize, initialFilters);
    };

    const goBack = () => {
        props.history.push("/alliance_wars/preparations");
    };

    const isBusy = applyingAll || refreshingTroops || !!applyingId;

    const openUseDefaultAllConfirm = () => {
        if (!seasonId || isBusy) {
            return;
        }
        setConfirmUseDefaultAllOpen(true);
    };

    const closeUseDefaultAllConfirm = () => {
        if (applyingAll) {
            return;
        }
        setConfirmUseDefaultAllOpen(false);
    };

    const openRefreshTroopsConfirm = () => {
        if (!seasonId || isBusy) {
            return;
        }
        setConfirmRefreshTroopsOpen(true);
    };

    const closeRefreshTroopsConfirm = () => {
        if (refreshingTroops) {
            return;
        }
        setConfirmRefreshTroopsOpen(false);
    };

    const handleRefreshTroops = async () => {
        if (!seasonId || isBusy) {
            return;
        }

        setRefreshingTroops(true);

        try {
            const response = await axios.post(
                `${Config.getInstance.apiUrl}/api/alliance_wars/seasons/${seasonId}/preparations/refresh-troops`,
                {},
                {
                    headers: {
                        Authorization: Helpers.getSessionId(),
                    },
                },
            );

            if (!response.data?.success) {
                PopupMessage.error(response.data?.message || "Failed to refresh troops");
                return;
            }

            const data = response.data?.data;
            PopupMessage.success(
                response.data?.message
                || `Updated=${data?.updated || 0}, skipped=${data?.skipped || 0}, failed=${data?.failed || 0}`,
            );
            setConfirmRefreshTroopsOpen(false);
            loadPreparations(page + 1, pageSize, appliedFilters);
        } catch (err: any) {
            PopupMessage.error(
                err?.response?.data?.message || err?.message || "Failed to refresh troops",
            );
        } finally {
            setRefreshingTroops(false);
        }
    };

    const handleUseDefaultAll = async () => {
        if (!seasonId || isBusy) {
            return;
        }

        setApplyingAll(true);

        try {
            const response = await axios.post(
                `${Config.getInstance.apiUrl}/api/alliance_wars/seasons/${seasonId}/preparations/use-default-all`,
                {},
                {
                    headers: {
                        Authorization: Helpers.getSessionId(),
                    },
                },
            );

            if (!response.data?.success) {
                PopupMessage.error(response.data?.message || "Failed to apply use default all");
                return;
            }

            const data = response.data?.data;
            PopupMessage.success(
                response.data?.message
                || `Applied=${data?.applied || 0}, skipped=${data?.skipped || 0}, failed=${data?.failed || 0}`,
            );
            setConfirmUseDefaultAllOpen(false);
            loadPreparations(page + 1, pageSize, appliedFilters);
        } catch (err: any) {
            PopupMessage.error(
                err?.response?.data?.message || err?.message || "Failed to apply use default all",
            );
        } finally {
            setApplyingAll(false);
        }
    };

    const handleUseDefault = async (item: AllianceWarPreparationItem) => {
        const preparationId = item.preparationId || item.id;
        if (!preparationId || applyingId) {
            return;
        }

        const skipParts = [];
        if (item.layoutDone) {
            skipParts.push("layout (already done)");
        }
        if (item.participantsDone) {
            skipParts.push("enlist (already done)");
        }

        const confirmMessage = skipParts.length > 0
            ? `Apply use default for this preparation?\nWill skip: ${skipParts.join(", ")}`
            : "Apply use default for layout and enlist on this preparation?";

        if (!window.confirm(confirmMessage)) {
            return;
        }

        setApplyingId(preparationId);

        try {
            const response = await axios.post(
                `${Config.getInstance.apiUrl}/api/alliance_wars/preparations/${preparationId}/use-default`,
                {},
                {
                    headers: {
                        Authorization: Helpers.getSessionId(),
                    },
                },
            );

            if (!response.data?.success) {
                PopupMessage.error(response.data?.message || "Failed to apply use default");
                return;
            }

            PopupMessage.success(response.data?.message || "Use default applied");
            loadPreparations(page + 1, pageSize, appliedFilters);
        } catch (err: any) {
            PopupMessage.error(
                err?.response?.data?.message || err?.message || "Failed to apply use default",
            );
        } finally {
            setApplyingId(null);
        }
    };

    return (
        <div className="contentPadding">
            <Paper className="root" elevation={0}>
                <div style={{ padding: "15px 22px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                        <Typography variant="h6">
                            Alliance wars / Preparations / {season?.name || seasonId}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {seasonId}
                            {season?.status ? ` · ${season.status}` : ""}
                        </Typography>
                    </div>
                    <div>
                        <Button disableElevation variant="outlined" onClick={goBack} style={{ marginRight: 8 }}>
                            Back
                        </Button>
                        <Button
                            disableElevation
                            variant="contained"
                            color="primary"
                            onClick={() => loadPreparations(page + 1, pageSize, appliedFilters)}
                        >
                            Refresh
                        </Button>
                    </div>
                </div>
            </Paper>

            <Paper className="root" elevation={0} style={{ marginTop: 16, padding: 22 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Squad ID"
                            value={filters.squadId}
                            onChange={handleFilterChange("squadId")}
                            placeholder="Search by squad id"
                            onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                    handleSearch();
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Preparation ID"
                            value={filters.preparationId}
                            onChange={handleFilterChange("preparationId")}
                            placeholder="Search by preparation id"
                            onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                    handleSearch();
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Button
                            disableElevation
                            variant="contained"
                            color="primary"
                            onClick={handleSearch}
                            style={{ marginRight: 8 }}
                        >
                            Search
                        </Button>
                        <Button
                            disableElevation
                            variant="contained"
                            color="secondary"
                            onClick={handleClear}
                        >
                            Clear
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            <Paper className="root" elevation={0} style={{ marginTop: 16, padding: 22 }}>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 16 }}>
                    <Button
                        disableElevation
                        variant="outlined"
                        color="primary"
                        disabled={isBusy || loading}
                        onClick={openRefreshTroopsConfirm}
                    >
                        {refreshingTroops ? "Refreshing..." : "Refresh Troop"}
                    </Button>
                    <Button
                        disableElevation
                        variant="contained"
                        color="secondary"
                        disabled={isBusy || loading}
                        onClick={openUseDefaultAllConfirm}
                    >
                        {applyingAll ? "Applying all..." : "Use Default All"}
                    </Button>
                </div>

                <Dialog
                    open={confirmUseDefaultAllOpen}
                    onClose={closeUseDefaultAllConfirm}
                    aria-labelledby="use-default-all-dialog-title"
                >
                    <DialogTitle id="use-default-all-dialog-title">
                        Confirm Use Default All
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Apply Use Default to <strong>all PENDING preparations</strong> in this season?
                        </DialogContentText>
                        <DialogContentText style={{ marginTop: 8 }}>
                            Rules still apply:
                        </DialogContentText>
                        <ul style={{ marginTop: 4, marginBottom: 0, paddingLeft: 20 }}>
                            <li>Skip layout if layout is already done</li>
                            <li>Skip enlist if enlist is already done</li>
                        </ul>
                        <DialogContentText style={{ marginTop: 12 }}>
                            Season: {season?.name || seasonId}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeUseDefaultAllConfirm} disabled={applyingAll}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleUseDefaultAll}
                            color="secondary"
                            variant="contained"
                            disableElevation
                            disabled={applyingAll}
                            autoFocus
                        >
                            {applyingAll ? "Applying..." : "Confirm"}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={confirmRefreshTroopsOpen}
                    onClose={closeRefreshTroopsConfirm}
                    aria-labelledby="refresh-troops-dialog-title"
                >
                    <DialogTitle id="refresh-troops-dialog-title">
                        Confirm Refresh Troop
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Refresh troop amount and level for <strong>all preparations</strong> in this season?
                        </DialogContentText>
                        <DialogContentText style={{ marginTop: 8 }}>
                            For each troop in a preparation, match by <strong>id</strong> against
                            {" "}<code>preparationTroopConfigs</code> and update <strong>amount</strong> and <strong>level</strong> to the config values.
                        </DialogContentText>
                        <DialogContentText style={{ marginTop: 12 }}>
                            Season: {season?.name || seasonId}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeRefreshTroopsConfirm} disabled={refreshingTroops}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleRefreshTroops}
                            color="primary"
                            variant="contained"
                            disableElevation
                            disabled={refreshingTroops}
                            autoFocus
                        >
                            {refreshingTroops ? "Refreshing..." : "Confirm"}
                        </Button>
                    </DialogActions>
                </Dialog>

                <LoadingWrapper loading={loading} error={error}>
                    {items.length === 0 ? (
                        <Typography>No preparations</Typography>
                    ) : (
                        <Table className="min-table-size" size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Preparation ID</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Tag</TableCell>
                                    <TableCell>Country</TableCell>
                                    <TableCell>Members</TableCell>
                                    <TableCell>Users enlist</TableCell>
                                    <TableCell>Layout done</TableCell>
                                    <TableCell>Participants done</TableCell>
                                    <TableCell>Is ready</TableCell>
                                    <TableCell>Leader</TableCell>
                                    <TableCell>Volunteer count</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map((item) => {
                                    const preparationId = item.preparationId || item.id;
                                    const isPending = String(item.status || "").toUpperCase() === "PENDING";
                                    const isApplying = applyingId === preparationId;

                                    return (
                                        <TableRow hover key={preparationId}>
                                            <TableCell>{preparationId}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{item.name}</Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    squad: {item.squadId}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{item.tag || "-"}</TableCell>
                                            <TableCell>{item.country || "-"}</TableCell>
                                            <TableCell>{item.membersCount}</TableCell>
                                            <TableCell>{item.usersEnlist}</TableCell>
                                            <TableCell>{boolChip(item.layoutDone)}</TableCell>
                                            <TableCell>{boolChip(item.participantsDone)}</TableCell>
                                            <TableCell>{boolChip(item.isReady)}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{item.leaderName || "-"}</Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {item.leaderUserId || "n/a"}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{item.volunteerCount}</TableCell>
                                            <TableCell>{item.status || "-"}</TableCell>
                                            <TableCell>
                                                {isPending ? (
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        color="primary"
                                                        disableElevation
                                                        disabled={isApplying || isBusy}
                                                        onClick={() => handleUseDefault(item)}
                                                    >
                                                        {isApplying ? "Applying..." : "Use Default"}
                                                    </Button>
                                                ) : (
                                                    "-"
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}

                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_event, nextPage) => loadPreparations(nextPage + 1, pageSize, appliedFilters)}
                        rowsPerPage={pageSize}
                        onRowsPerPageChange={(event) => loadPreparations(1, parseInt(event.target.value, 10), appliedFilters)}
                        rowsPerPageOptions={[5, 10, 20, 50, 100]}
                    />
                </LoadingWrapper>
            </Paper>
        </div>
    );
};
